import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { useContentId } from '@/components/SlugGate';
import Navbar from '@/components/Navbar';
import AdBanner from '@/components/AdBanner';
import ReportModal from '@/components/ReportModal';
import ShareButtons from '@/components/ShareButtons';
import SEOHead from '@/components/SEOHead';
import { storyJsonLd } from '@/lib/jsonLd';
import { ReadingTime } from '@/components/ReadingTime';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Play, 
  Heart, 
  Star, 
  Eye, 
  Lock, 
  ChevronLeft,
  MessageCircle,
  Flag,
  Sparkles
} from 'lucide-react';

function StoryDetail() {
  const id = useContentId();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [story, setStory] = useState(null);
  const [comments, setComments] = useState([]);
  const [storyAds, setStoryAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const storyRes = await api.get('/stories/' + id);
        setStory(storyRes.data);
        
        const commentsRes = await api.get('/stories/' + id + '/comments');
        setComments(commentsRes.data);

        const adsRes = await api.get('/ads?placement=story&limit=2');
        setStoryAds(adsRes.data);

        if (user) {
          const favRes = await api.get('/favorites');
          const list = favRes.data || [];
          let found = false;
          for (let i = 0; i < list.length; i++) {
            if (list[i].id === id) {
              found = true;
              break;
            }
          }
          setIsFavorite(found);
        }
      } catch (err) {
        toast.error('Erro ao carregar o conto');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id, user]);

  async function handleFavorite() {
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      if (isFavorite) {
        await api.delete('/favorites/' + id);
        setIsFavorite(false);
        toast.success('Removido dos favoritos');
      } else {
        await api.post('/favorites/' + id);
        setIsFavorite(true);
        toast.success('Adicionado aos favoritos');
        // Award points for adding favorite
        try {
          await api.post('/points/earn/add_favorite?reference_id=' + id);
        } catch (e) { /* ignore */ }
      }
    } catch (err) {
      toast.error('Erro ao atualizar favoritos');
    }
  }

  async function handleRate(rating) {
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      await api.post('/stories/' + id + '/rate', { story_id: id, rating: rating });
      setUserRating(rating);
      toast.success('Avaliação registrada!');
      const res = await api.get('/stories/' + id);
      setStory(res.data);
    } catch (err) {
      toast.error('Erro ao avaliar');
    }
  }

  async function handleComment(e) {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }
    if (!newComment.trim()) return;
    if (newComment.trim().length < 10) {
      toast.error('O comentário deve ter pelo menos 10 caracteres');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/stories/' + id + '/comments', {
        content: newComment
      });
      setNewComment('');
      toast.success('Comentário enviado para moderação. Será visível após aprovação.');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao comentar';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  }

  function handleRead() {
    if (!user) {
      navigate('/login');
      return;
    }
    // Trust the backend flag — it already accounts for Premium AND
    // admin-granted specific access.
    if (story.is_locked) {
      navigate('/premium');
      return;
    }
    navigate('/read/' + (story?.slug || id));
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ruby"></div>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Conto não encontrado</p>
      </div>
    );
  }

  // is_locked from the backend already reflects Premium + specific-access
  // grants — do NOT recompute from user.is_premium client-side.
  const needsPremium = !!story.is_locked;

  const categoryBadges = [];
  for (let i = 0; i < story.categories.length; i++) {
    categoryBadges.push(
      <Badge key={'cat-' + i} variant="secondary" className="bg-surface-light">{story.categories[i]}</Badge>
    );
  }

  const tagBadges = [];
  for (let i = 0; i < story.tags.length; i++) {
    tagBadges.push(
      <Badge key={'tag-' + i} variant="outline" className="border-border">#{story.tags[i]}</Badge>
    );
  }

  const ratingButtons = [];
  for (let star = 1; star <= 5; star++) {
    const isActive = star <= userRating;
    const starNum = star;
    ratingButtons.push(
      <button
        key={'star-' + star}
        onClick={function() { handleRate(starNum); }}
        data-testid={'rate-' + star}
        className="p-2 hover:scale-110 transition-transform"
      >
        <Star className={'w-8 h-8 ' + (isActive ? 'text-gold fill-gold' : 'text-gray-600')} />
      </button>
    );
  }

  const commentsList = [];
  for (let i = 0; i < comments.length; i++) {
    const comment = comments[i];
    commentsList.push(
      <div key={'comment-' + i} className="bg-surface rounded-lg p-4 border border-border">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 rounded-full bg-ruby/20 flex items-center justify-center">
            <span className="text-ruby font-semibold text-sm">
              {comment.user_name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-medium text-foreground">{comment.user_name}</p>
            <p className="text-xs text-muted-foreground">
              {new Date(comment.created_at).toLocaleDateString('pt-BR')}
            </p>
          </div>
        </div>
        <p className="text-muted-foreground">{comment.content}</p>
      </div>
    );
  }

  const adsList = [];
  for (let i = 0; i < storyAds.length; i++) {
    adsList.push(
      <AdBanner key={'story-ad-' + i} ad={storyAds[i]} variant="sidebar" />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={story.title}
        description={story.synopsis || story.excerpt}
        image={story.cover_image}
        url={`${window.location.origin}/story/${story.slug || story.id}`}
        jsonLd={storyJsonLd(story)}
      />
      <Navbar />

      <section className="relative h-[50vh] min-h-[400px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(' + story.cover_image + ')' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-black/50" />

        <Button
          variant="ghost"
          onClick={function() { navigate(-1); }}
          data-testid="back-btn"
          className="absolute top-20 left-6 text-foreground hover:bg-white/10 z-10"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Voltar
        </Button>
      </section>

      <div className="relative z-10 -mt-32 pb-12 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-shrink-0">
                  <div className="w-48 aspect-[2/3] rounded-lg overflow-hidden shadow-2xl">
                    <img src={story.cover_image} alt={story.title} className="w-full h-full object-cover" />
                  </div>
                </div>

                <div className="flex-1">
                  {story.is_premium ? (
                    <span className="premium-badge inline-block mb-3">
                      <Lock className="w-3 h-3 inline mr-1" />Premium
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 mb-3 bg-emerald-500/95 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                      <Sparkles className="w-3 h-3" />Grátis
                    </span>
                  )}
                  
                  <h1 className="font-brand text-3xl md:text-4xl font-bold text-foreground mb-2">{story.title}</h1>
                  <p className="text-muted-foreground mb-4">por {story.author_name}</p>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
                    <ReadingTime minutes={story.reading_time} className="text-muted-foreground" />
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />{story.views.toLocaleString()} visualizações
                    </span>
                    {story.rating > 0 && (
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-gold fill-gold" />
                        {story.rating.toFixed(1)} ({story.rating_count} avaliações)
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {categoryBadges}
                    {tagBadges}
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={handleRead}
                      data-testid="read-story-btn"
                      className="bg-ruby hover:bg-ruby-light text-white rounded-full px-8 py-6"
                    >
                      {needsPremium ? (
                        <span className="flex items-center"><Lock className="w-5 h-5 mr-2" />Assinar para Ler</span>
                      ) : (
                        <span className="flex items-center"><Play className="w-5 h-5 mr-2 fill-white" />Ler Agora</span>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleFavorite}
                      data-testid="favorite-btn"
                      className={'rounded-full px-6 py-6 ' + (isFavorite ? 'border-ruby text-ruby' : 'border-border')}
                    >
                      <Heart className={'w-5 h-5 ' + (isFavorite ? 'fill-ruby' : '')} />
                    </Button>
                    <ShareButtons
                      url={`${window.location.origin}/story/${story.slug || story.id}`}
                      title={`${story.title} - Intimate.pt`}
                      description={story.synopsis?.substring(0, 150) || `Lê "${story.title}" no Intimate.pt`}
                      variant="popover"
                      size="md"
                      className="rounded-full border-border"
                    />
                    {user && story.author_id !== user.id && (
                      <Button
                        variant="outline"
                        onClick={() => setReportModalOpen(true)}
                        data-testid="report-story-btn"
                        className="rounded-full px-6 py-6 border-border hover:border-red-500 hover:text-red-500"
                      >
                        <Flag className="w-5 h-5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-12">
                <h2 className="font-brand text-xl font-semibold text-foreground mb-4">Sinopse</h2>
                <p className="text-muted-foreground font-reading leading-relaxed">{story.synopsis}</p>
              </div>

              <div className="mt-12">
                <h2 className="font-brand text-xl font-semibold text-foreground mb-4">Avaliar</h2>
                <div className="flex gap-2">{ratingButtons}</div>
              </div>

              <div className="mt-12">
                <h2 className="font-brand text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Comentários ({comments.length})
                </h2>

                {user ? (
                  <form onSubmit={handleComment} className="mb-8">
                    <textarea
                      value={newComment}
                      onChange={function(e) { setNewComment(e.target.value); }}
                      placeholder="Escreve o seu comentário... (mínimo 10 caracteres)"
                      data-testid="comment-input"
                      className="w-full bg-surface border border-border rounded-lg p-4 text-foreground placeholder:text-muted-foreground focus:border-ruby focus:ring-1 focus:ring-ruby resize-none"
                      rows={3}
                      maxLength={2000}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-muted-foreground">
                        {newComment.length}/2000 caracteres • Os comentários são moderados antes de serem publicados
                      </p>
                      <Button
                        type="submit"
                        disabled={submitting || newComment.trim().length < 10}
                        data-testid="submit-comment"
                        className="bg-ruby hover:bg-ruby-light rounded-full"
                      >
                        {submitting ? 'A enviar...' : 'Enviar Comentário'}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <p className="text-muted-foreground mb-8">
                    <Link to="/login" className="text-ruby hover:underline">Inicie sessão</Link> para comentar.
                  </p>
                )}

                <div className="space-y-4">
                  {comments.length > 0 ? commentsList : (
                    <p className="text-muted-foreground text-center py-8">
                      Nenhum comentário ainda. Seja o primeiro a comentar!
                    </p>
                  )}
                </div>
              </div>
            </div>

            {storyAds.length > 0 && (
              <div className="lg:w-72 flex-shrink-0">
                <div className="sticky top-24 space-y-4">
                  {adsList}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        contentType="story"
        contentId={story?.id}
        contentTitle={story?.title}
      />
    </div>
  );
}

export default StoryDetail;
